import _extends from "@babel/runtime/helpers/extends";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;

import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { calculateRem } from '@uie/javascript-style-helpers';
import { TitleConfig, TypographyConfig } from '@vds-core/typography';
import { colors } from '@vds-core/theme';
import { withVDSManager } from '@vds-core/utilities';

function _calculateButtonHeight(size) {
  var height;

  if (size === 'small') {
    height = calculateRem(24);
  } else {
    height = calculateRem(42);
  }

  return height;
}

function _calculateSideMargin(size, width) {
  var sideMargin, verticalMargin;

  if (size === 'small') {
    sideMargin = width === 'autoTight' ? 12 : 24;
    verticalMargin = 5;
  } else {
    sideMargin = width === 'autoTight' ? 21 : 42;
    verticalMargin = 13;
  }

  return calculateRem(verticalMargin, sideMargin);
}

var fontWeightStrong = 750;
var propTypes = {
  /**
   * If true, will render a Primary button (default).
   */
  primary: PropTypes.bool,

  /**
   * If true, will render a Secondary Button.
   */
  secondary: PropTypes.bool,

  /**
   * Accepts a string value to render as the text inside button.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,

  /**
   * If true, button will render as disabled.
   */
  disabled: PropTypes.bool,

  /**
   *  Button will be displayed in inverted colors if inverted prop is passed.
   */
  inverted: PropTypes.bool,

  /**
   * Callback function executed when button is clicked.
   */
  onClick: PropTypes.func,

  /**
   * Accepts small or large and renders the associated button size.
   */
  size: PropTypes.oneOf(['small', 'large']),

  /**
   * Accepts a string or number value if rendering the button at a fixed size.
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Changes the display property of the button.  Accepts flex, inline-block and block.
   */
  display: PropTypes.oneOf(['flex', 'inline-block', 'block']),

  /**
   * Aria label used for the button.
   */
  ariaLabel: PropTypes.string,

  /**
   * @ignore
   */
  bodyConfig: PropTypes.object,

  /**
   * @ignore
   */
  typescale: PropTypes.string,

  /**
   * @ignore
   */
  colorConfig: PropTypes.object,

  /**
   * @ignore
   */
  focusState: PropTypes.bool,

  /**
   * @ignore
   */
  hoverState: PropTypes.bool,

  /**
   * @ignore
   */
  activeState: PropTypes.bool,

  /**
   * @ignore
   */
  hitArea: PropTypes.bool,

  /**
   * @ignore
   */
  calculateButtonHeight: PropTypes.func,

  /**
   * @ignore
   */
  calculateSideMargin: PropTypes.func,

  /**
   * @ignore
   */
  smallButtonPadding: PropTypes.bool,

  /**
   * @ignore
   */
  overflowEllipsis: PropTypes.bool,

  /**
   * @ignore
   */
  calculateFontWeight: PropTypes.number,

  /**
   * Allows a string to be provided for analytics.
   */
  'data-analyticstrack': PropTypes.string,

  /**
   * Allows a string to be provided for analytics.
   */
  'data-track': PropTypes.string,

  /**
   * Allows a string to be provided for click stream.
   */
  'data-clickstream': PropTypes.string,

  /**
   * @ignore
   */
  dataLoc: PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-level': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-position': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-datatrack': PropTypes.string,

  /**
   * Allows a string to be provided for analytics tagging.
   */
  'data-sitecat-cta': PropTypes.string,

  /**
   * If provided, button primitive becomes an anchor, with the given href attribute.
   */
  href: PropTypes.string
};
var defaultProps = {
  primary: false,
  secondary: false,
  children: null,
  disabled: false,
  size: 'large',
  display: 'flex',
  width: 'auto',
  onClick: undefined,
  bodyConfig: TitleConfig,
  colorConfig: colors,
  hitArea: false,
  calculateButtonHeight: _calculateButtonHeight,
  calculateSideMargin: _calculateSideMargin,
  hoverState: false,
  smallButtonPadding: true,
  overflowEllipsis: false,
  calculateFontWeight: fontWeightStrong,
  typescale: TypographyConfig.getTypescale(),
  focusState: false,
  activeState: false
};
/**
 * @component
 * */

var HitArea = /*#__PURE__*/styled.span.withConfig({
  displayName: "HitArea",
  componentId: "sc-bc3yhn-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  height: ", ";\n  width: 100%;\n  left: 50%;\n  position: absolute;\n  transform: translate(-50%, -50%);\n  text-align: center;\n  top: 50%;\n  content: '';\n  display: inline-block;\n  &:hover {\n    cursor: pointer;\n  }\n"])), calculateRem(44));
/* 
This must be display: inline-block to allow for overflow ellipsis to show. 
Padding top/bottom is necessary for text to be centered within button. 
StyledChildWrapper needs to have 100% height because it needs to be the click event target element
*/

var StyledChildWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledChildWrapper",
  componentId: "sc-bc3yhn-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  position: relative;\n  height: 100%;\n  width: 100%;\n  border-radius: ", ";\n  box-sizing: border-box;\n  display: inline-block;\n  vertical-align: middle;\n  align-items: center;\n  background: transparent;\n  justify-content: center;\n  overflow: ", ";\n  text-overflow: ", ";\n  -webkit-tap-highlight-color: transparent;\n  &:focus {\n    outline: none;\n  }\n  ", ";\n  &:hover {\n    outline: none;\n    border: none;\n  }\n"])), calculateRem(100), function (_ref) {
  var overflowEllipsis = _ref.overflowEllipsis;
  return overflowEllipsis && 'hidden';
}, function (_ref2) {
  var overflowEllipsis = _ref2.overflowEllipsis;
  return overflowEllipsis && 'ellipsis';
}, function (_ref3) {
  var size = _ref3.size,
      width = _ref3.width,
      calculateSideMargin = _ref3.calculateSideMargin,
      smallButtonPadding = _ref3.smallButtonPadding;

  switch (size) {
    case 'tiny':
      return "\n          margin: ".concat(calculateRem(0, 13), ";\n        ");

    case 'small':
      return "\n          padding: ".concat(calculateSideMargin(size, width), ";\n        ");

    case 'block':
      return "\n          display: block;\n          width: 100%;\n          padding: ".concat(calculateSideMargin(size, width), ";\n          margin: auto;\n        ");

    default:
      return "\n          padding: ".concat(calculateSideMargin(size, width), ";");
  }
});

var findWidth = function findWidth(width) {
  if (typeof width === 'string') return width;
  return calculateRem(width);
};

var buttonStyles = /*#__PURE__*/css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  padding: 0;\n  border-radius: ", ";\n  border-width: ", ";\n  box-sizing: border-box;\n  cursor: pointer;\n  display: ", ";\n  flex-direction: row;\n  align-items: center;\n  justify-content: center;\n  font-family: ", ";\n  font-size: ", ";\n  font-weight: ", ";\n  line-height: ", ";\n  letter-spacing: ", ";\n  height: ", ";\n  position: relative;\n  text-align: center;\n  text-decoration: none;\n  touch-action: ", ";\n  pointer-events: ", ";\n  vertical-align: middle;\n  width: auto;\n  overflow: visible;\n  -webkit-tap-highlight-color: transparent;\n\n  ", ";\n\n  background-color: ", ";\n  border: ", ";\n  color: ", ";\n  ", ";\n  ", ";\n  ", ";\n\n  ", ";\n\n  ", ";\n\n  ", ";\n"])), calculateRem(100), calculateRem(1), function (_ref4) {
  var display = _ref4.display;
  return display;
}, function (_ref5) {
  var largeButtonText = _ref5.largeButtonText;
  return largeButtonText.fontFamily;
}, function (_ref6) {
  var largeButtonText = _ref6.largeButtonText;
  return calculateRem(largeButtonText.fontSize);
}, function (_ref7) {
  var calculateFontWeight = _ref7.calculateFontWeight;
  return calculateFontWeight;
}, function (_ref8) {
  var largeButtonText = _ref8.largeButtonText;
  return calculateRem(largeButtonText.lineHeight);
}, function (_ref9) {
  var largeButtonText = _ref9.largeButtonText,
      size = _ref9.size;
  return size === 'large' && calculateRem(largeButtonText.letterSpacing);
}, function (_ref10) {
  var calculateButtonHeight = _ref10.calculateButtonHeight,
      size = _ref10.size;
  return calculateButtonHeight(size);
}, function (_ref11) {
  var disabled = _ref11.disabled;
  return disabled ? 'none' : 'manipulation';
}, function (_ref12) {
  var disabled = _ref12.disabled;
  return disabled && 'none';
}, function (_ref13) {
  var size = _ref13.size,
      width = _ref13.width,
      smallButtonText = _ref13.smallButtonText;

  if (size === 'tiny') {
    return "\n        font-size: ".concat(calculateRem(smallButtonText.fontSize), ";\n        line-height: ").concat(calculateRem(smallButtonText.lineHeight), ";\n        font-family: ").concat(smallButtonText.fontFamily, ";\n      ");
  }

  if (size === 'small') {
    return "\n        font-size: ".concat(calculateRem(smallButtonText.fontSize), ";\n        line-height: ").concat(calculateRem(smallButtonText.lineHeight), ";\n        font-family: ").concat(smallButtonText.fontFamily, ";\n        width: ").concat(findWidth(width), ";\n        white-space: nowrap;\n      ");
  }

  if (size === 'large') {
    return "\n        width: ".concat(findWidth(width), ";\n        white-space: nowrap;\n      ");
  }

  if (size === 'block') {
    return "\n        display: block;\n        width: 100%;\n      ";
  }
}, function (_ref14) {
  var colorConfig = _ref14.colorConfig,
      secondary = _ref14.secondary;
  return secondary ? 'transparent' : colorConfig.black;
}, function (_ref15) {
  var colorConfig = _ref15.colorConfig;
  return "".concat(calculateRem(1), " solid ").concat(colorConfig.black);
}, function (_ref16) {
  var colorConfig = _ref16.colorConfig,
      secondary = _ref16.secondary;
  return secondary ? colorConfig.black : colorConfig.white;
}, function (_ref17) {
  var activeState = _ref17.activeState,
      disabled = _ref17.disabled,
      colorConfig = _ref17.colorConfig,
      secondary = _ref17.secondary;
  return activeState && !disabled && "\n      &:active {\n        color: ".concat(secondary && colorConfig.coolGray6, ";\n        border-color: ").concat(colorConfig.coolGray6, ";\n        box-shadow: 0 0 0 ").concat(calculateRem(1), " ").concat(colorConfig.coolGray6, ";\n        background-color: ").concat(!secondary && colorConfig.coolGray6, ";\n      }\n    ");
}, function (_ref18) {
  var focusState = _ref18.focusState,
      disabled = _ref18.disabled,
      inverted = _ref18.inverted,
      colorConfig = _ref18.colorConfig;
  return focusState && !disabled && "\n     &:focus:not(:hover) {\n      outline: none;\n      &::before {\n        border: ".concat(calculateRem(1), " dashed ").concat(inverted ? colorConfig.white : colorConfig.black, ";\n        border-radius: ").concat(calculateRem(100), ";\n        content: '';\n        height: calc(100% + ").concat(calculateRem(6), ");\n        left: 50%;\n        position: absolute;\n        top: 50%;\n        transform: translate(-50%, -50%);\n        width: calc(100% + ").concat(calculateRem(6), ");\n      }\n    }\n    ");
}, function (_ref19) {
  var hoverState = _ref19.hoverState,
      colorConfig = _ref19.colorConfig,
      inverted = _ref19.inverted,
      disabled = _ref19.disabled;
  return hoverState && !disabled && "\n    &:hover:not(:active) {\n      outline: none;\n      box-shadow: 0 0 0 ".concat(calculateRem(1), " ").concat(inverted ? colorConfig.white : colorConfig.black, ";\n      transition: ease-out 0.1s;\n    }\n    &:hover:not(:active) span {\n      border: none;\n    }\n  ");
}, function (_ref20) {
  var inverted = _ref20.inverted,
      disabled = _ref20.disabled,
      activeState = _ref20.activeState,
      colorConfig = _ref20.colorConfig,
      secondary = _ref20.secondary;
  return inverted && !disabled && "\n    background-color: ".concat(secondary ? 'transparent' : colorConfig.white, ";\n    border: ").concat(calculateRem(1), " solid ").concat(colorConfig.white, ";\n    color: ").concat(secondary ? colorConfig.white : colorConfig.black, ";\n\n  ");
}, function (_ref21) {
  var disabled = _ref21.disabled,
      colorConfig = _ref21.colorConfig,
      activeState = _ref21.activeState,
      secondary = _ref21.secondary;
  return disabled && "\n    background-color: ".concat(secondary ? 'transparent' : colorConfig.coolGray3, ";\n    border-color: ").concat(colorConfig.coolGray3, ";\n    color: ").concat(secondary ? colorConfig.coolGray3 : colorConfig.white, ";\n    cursor: default;\n  ");
}, function (_ref22) {
  var disabled = _ref22.disabled,
      inverted = _ref22.inverted,
      colorConfig = _ref22.colorConfig,
      activeState = _ref22.activeState,
      secondary = _ref22.secondary;
  return disabled && inverted && "\n    background-color: ".concat(secondary ? 'transparent' : colorConfig.coolGray10, ";\n    border-color: ").concat(colorConfig.coolGray10, ";\n    color: ").concat(secondary ? colorConfig.coolGray10 : colorConfig.black, "\n  ");
});
var StyledButton = /*#__PURE__*/styled.button.withConfig({
  displayName: "StyledButton",
  componentId: "sc-bc3yhn-2"
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  ", ";\n"])), buttonStyles);
var StyledAnchor = /*#__PURE__*/styled.a.withConfig({
  displayName: "StyledAnchor",
  componentId: "sc-bc3yhn-3"
})(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  ", ";\n  width: ", ";\n  justify-content: center;\n"])), buttonStyles, function (_ref23) {
  var width = _ref23.width,
      size = _ref23.size;
  return width !== 'auto' && width !== 'autoTight' && size === 'large' ? findWidth(width) : 'fit-content';
});

var Button = function Button(props) {
  var children = props.children,
      size = props.size,
      width = props.width,
      disabled = props.disabled,
      ariaLabel = props.ariaLabel,
      onClick = props.onClick,
      display = props.display,
      colorConfig = props.colorConfig,
      bodyConfig = props.bodyConfig,
      hitArea = props.hitArea,
      typescale = props.typescale,
      calculateButtonHeight = props.calculateButtonHeight,
      calculateSideMargin = props.calculateSideMargin,
      smallButtonPadding = props.smallButtonPadding,
      calculateFontWeight = props.calculateFontWeight,
      overflowEllipsis = props.overflowEllipsis,
      dataLoc = props.dataLoc,
      secondary = props.secondary,
      href = props.href;
  var largeButtonText, smallButtonText;
  largeButtonText = bodyConfig.getStyles(typescale, 'desktop', typescale === 'VDS' ? 'large' : 'extraSmall');
  smallButtonText = bodyConfig.getStyles(typescale, 'mobile', typescale === 'VDS' ? 'small' : 'extraSmall');
  var newAriaLabel = ariaLabel ? ariaLabel : typeof children === 'string' ? children : 'Button';
  /* 
    The change to add a tabIndex of -1 on the ButtonBase element is because while button primitives 
    natively will render a disabled button non-focusable. The case when using a href this element is an atag. For this, we need to restrict it's focus.
  */

  var ButtonBase = href ? StyledAnchor : StyledButton;
  return /*#__PURE__*/React.createElement(ButtonBase, _extends({}, props, {
    href: href,
    size: size,
    width: width,
    "aria-label": newAriaLabel,
    role: "button",
    "aria-disabled": props.disabled,
    tabIndex: disabled ? -1 : 0,
    display: display,
    largeButtonText: largeButtonText,
    smallButtonText: smallButtonText,
    colorConfig: colorConfig,
    disabled: disabled,
    calculateButtonHeight: calculateButtonHeight,
    calculateSideMargin: calculateSideMargin,
    calculateFontWeight: calculateFontWeight,
    "data-loc": dataLoc,
    secondary: secondary
  }), hitArea && !disabled && /*#__PURE__*/React.createElement(HitArea, null), /*#__PURE__*/React.createElement(StyledChildWrapper, {
    tabIndex: -1,
    display: display,
    size: size,
    width: width,
    overflowEllipsis: overflowEllipsis,
    calculateSideMargin: calculateSideMargin,
    smallButtonPadding: smallButtonPadding
  }, children));
};

Button.defaultProps = defaultProps;
Button.propTypes = propTypes;
export default withVDSManager(Button);